import type { BlogQueryParams, BlogResponse } from "../Types/blog.types";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/$/, "");

class BlogService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/blogs`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get all blogs with filters
  async getAllBlogs(params?: BlogQueryParams): Promise<BlogResponse> {
    const queryParams: Record<string, string> = {};

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            queryParams[key] = value.join(",");
          } else if (typeof value === "boolean") {
            queryParams[key] = value.toString();
          } else {
            queryParams[key] = String(value);
          }
        }
      });
    }

    const queryString = new URLSearchParams(queryParams).toString();
    return this.request<BlogResponse>(
      `${queryString ? `?${queryString}` : ""}`,
    );
  }

  // Get blog by slug
  async getBlogBySlug(slug: string): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/slug/${slug}`);
  }

  // Get featured blogs
  async getFeaturedBlogs(limit: number = 3): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/featured?limit=${limit}`);
  }

  // Get popular blogs (sorted by views and likes)
  async getPopularBlogs(limit: number = 5): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/popular?limit=${limit}`);
  }

  // Get recent blogs (sorted by published date)
  async getRecentBlogs(limit: number = 5): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/recent?limit=${limit}`);
  }

  // Search blogs
  async searchBlogs(query: string, limit: number = 10): Promise<BlogResponse> {
    return this.request<BlogResponse>(
      `/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  }

  // Get related blogs by slug
  async getRelatedBlogs(
    slug: string,
    limit: number = 3,
  ): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/${slug}/related?limit=${limit}`);
  }

  // Increment views
  async incrementViews(id: string): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/${id}/views`, {
      method: "POST",
    });
  }

  // Increment likes (toggle like on)
  async incrementLikes(id: string): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/${id}/likes`, {
      method: "POST",
      body: JSON.stringify({ increment: true }),
    });
  }

  // Decrement likes (toggle like off)
  async decrementLikes(id: string): Promise<BlogResponse> {
    return this.request<BlogResponse>(`/${id}/likes`, {
      method: "POST",
      body: JSON.stringify({ increment: false }),
    });
  }
}

const blogService = new BlogService();

export default blogService;
